import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Activity, Users, FileText, LogOut, Eye, Download,
  TrendingUp, CheckCircle, Clock, AlertCircle
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();
  const [allAssessments, setAllAssessments] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAssessments: 0,
    completedToday: 0,
    inProgress: 0
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    // Check if admin
    if (!isAdmin) {
      navigate('/user-dashboard');
      return;
    }

    loadAllAssessments();
  }, [user, isAdmin, navigate]);

  const loadAllAssessments = async () => {
    try {
      // Fetch from database API
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'assessment.list' })
      });

      if (!response.ok) throw new Error('Failed to fetch assessments');

      const { assessments } = await response.json();
      setAllAssessments(assessments || []);

      // Fetch user count
      const usersResponse = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'user.list' })
      });

      const { users } = await usersResponse.json();

      // Calculate stats
      const today = new Date().toDateString();
      const completedToday = assessments.filter(
        a => a.completed_at && new Date(a.completed_at).toDateString() === today
      ).length;
      const inProgress = assessments.filter(a => a.status === 'in_progress').length;

      setStats({
        totalUsers: users?.length || 0,
        totalAssessments: assessments?.length || 0,
        completedToday,
        inProgress
      });
    } catch (error) {
      console.error('Error loading assessments:', error);
      // Fallback to localStorage if API fails
      const stored = localStorage.getItem('health_assessments');
      if (stored) {
        const assessments = Object.values(JSON.parse(stored));
        setAllAssessments(assessments);
      }
    }
  };

  const handleLogout = async () => {
    await signOut();
    // Use replace to prevent back button issues and immediate navigation
    navigate('/', { replace: true });
  };

  const handleViewReport = (assessmentId) => {
    navigate(`/report/${assessmentId}`);
  };

  const getCompletionStatus = (assessment) => {
    // Handle both camelCase (localStorage) and snake_case (database) formats
    const nutritionData = assessment.nutrition_responses || assessment.nutritionResponses;
    const cognitiveData = assessment.cognitive_responses || assessment.cognitiveResponses;
    const physicalData = assessment.physical_tests || assessment.physicalTests;
    
    const hasNutrition = nutritionData?.length > 0;
    const hasCognitive = cognitiveData?.length > 0;
    const hasPhysical = physicalData?.length > 0;
    const completed = hasNutrition && hasCognitive && hasPhysical;
    return { hasNutrition, hasCognitive, hasPhysical, completed };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-400" />
              <div>
                <h1 className="text-xl font-bold text-white">Splink Pro - Admin Dashboard</h1>
                <p className="text-xs text-slate-400">Administrator Panel</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.displayName || 'Admin'}</p>
                <p className="text-xs text-green-400">Administrator</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8" />
              <TrendingUp className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-3xl font-bold mb-1">{stats.totalUsers}</p>
            <p className="text-sm text-blue-100">Total Users</p>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-8 h-8" />
              <CheckCircle className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-3xl font-bold mb-1">{stats.totalAssessments}</p>
            <p className="text-sm text-purple-100">Total Assessments</p>
          </div>

          <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8" />
              <Clock className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-3xl font-bold mb-1">{stats.completedToday}</p>
            <p className="text-sm text-green-100">Completed Today</p>
          </div>

          <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="w-8 h-8" />
              <Activity className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-3xl font-bold mb-1">{stats.inProgress}</p>
            <p className="text-sm text-orange-100">In Progress</p>
          </div>
        </div>

        {/* All Assessments Table */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-bold text-white">All Assessments</h2>
            <p className="text-sm text-slate-400 mt-1">View and manage all user assessments</p>
          </div>

          <div className="overflow-x-auto">
            {allAssessments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No assessments yet</p>
                <p className="text-slate-500 text-sm">Assessments will appear here once users complete them</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Sport
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {allAssessments.map((assessment) => {
                    const status = getCompletionStatus(assessment);
                    return (
                      <tr key={assessment.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            {(assessment.personal_info || assessment.personalInfo)?.firstName} {(assessment.personal_info || assessment.personalInfo)?.lastName}
                          </div>
                          <div className="text-xs text-slate-400">
                            {(assessment.personal_info || assessment.personalInfo)?.schoolName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                          {(assessment.personal_info || assessment.personalInfo)?.email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                          {(assessment.personal_info || assessment.personalInfo)?.sport || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                          {new Date(assessment.created_at || assessment.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              status.hasNutrition
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-slate-700 text-slate-400'
                            }`}>
                              N
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              status.hasCognitive
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-slate-700 text-slate-400'
                            }`}>
                              C
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              status.hasPhysical
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-slate-700 text-slate-400'
                            }`}>
                              P
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewReport(assessment.id)}
                              className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                            >
                              <Eye className="w-3 h-3" />
                              <span>View</span>
                            </button>
                            <button
                              className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                            >
                              <Download className="w-3 h-3" />
                              <span>PDF</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
